import { useCallback, useEffect, useRef } from 'react';

export const LONG_PRESS_MS = 500;
/** Finger travel that turns a press into a scroll and cancels it. */
const MOVE_TOLERANCE_PX = 10;

/**
 * Touch handlers that call `onLongPress` after the finger rests for
 * LONG_PRESS_MS. iOS Safari fires no `contextmenu` on a long press, so
 * right-click menus need this to be reachable on phones.
 */
export const useLongPress = (onLongPress: () => void, delay: number = LONG_PRESS_MS) => {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const callback = useRef(onLongPress);
  callback.current = onLongPress;

  const cancel = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    origin.current = null;
  }, []);

  useEffect(() => cancel, [cancel]);

  const onTouchStart = useCallback(
    (event: React.TouchEvent) => {
      const touch = event.touches[0];
      origin.current = { x: touch.clientX, y: touch.clientY };
      fired.current = false;
      timer.current = setTimeout(() => {
        fired.current = true;
        callback.current();
      }, delay);
    },
    [delay],
  );

  const onTouchMove = useCallback(
    (event: React.TouchEvent) => {
      const touch = event.touches[0];
      if (
        origin.current &&
        Math.hypot(touch.clientX - origin.current.x, touch.clientY - origin.current.y) > MOVE_TOLERANCE_PX
      ) {
        cancel();
      }
    },
    [cancel],
  );

  const onTouchEnd = useCallback(
    (event: React.TouchEvent) => {
      // A long press must not also count as the tap that ends it.
      if (fired.current) event.preventDefault();
      cancel();
    },
    [cancel],
  );

  return { onTouchStart, onTouchMove, onTouchEnd, onTouchCancel: cancel };
};
